import { createContext, useContext, useEffect, useId, useMemo, useRef, useState, type KeyboardEvent } from 'react';
import { useNavigate } from 'react-router';
import { Search, SquareCheckBig, StickyNote, UserRound, X } from 'lucide-react';
import { ModalDialog } from '@/components/ModalDialog';
import type { NavGroup, NavItem } from '@/config/navigation';
import { flattenGroupItems } from '@/lib/sidebar-nav';
import { cn } from '@/lib/utils';
import { normalizeSearch } from '@domain/search';

/** Window event the header's search button dispatches to open the palette. */
export const OPEN_COMMAND_PALETTE_EVENT = 'h-command-palette:open';

/**
 * Not a module on the rail, but a destination every signed-in person has; the palette lists it with the rest.
 * (The prototype's palette lists the navigation only; its header has no account page to reach.)
 */
const PERSONAL_GROUP: NavGroup = {
  id: 'ca-nhan',
  label: 'Cá nhân',
  icon: UserRound,
  items: [{ id: 'tai-khoan-cua-toi', label: 'Tài khoản của tôi', icon: UserRound, href: '/account' }],
};

/**
 * Folds Vietnamese for matching (`@domain/search`): "đ" to "d", tone and vowel marks dropped, lower case. Typing
 * "cong viec" or "tai chinh" finds what it should without the person switching keyboard layouts.
 */
const fold = normalizeSearch;

type Entry = { group: NavGroup; item: NavItem };

/** A card the palette can offer under "Thẻ" (UX wave D, P7). */
export type PaletteCard = { id: string; title: string; note: boolean };

/**
 * Where the palette's cards come from. The frame issues no request and reads no query cache (app.tsx), so the signed-in
 * shell provides them (features/workplace/palette-cards.tsx): `cards` is what it holds, and `want` asks it to read
 * them — once a word is typed, never before. Without a provider (a bare frame) there is no "Thẻ" group.
 */
export type PaletteCards = { cards: readonly PaletteCard[]; want: () => void };
export const PaletteCardsContext = createContext<PaletteCards | null>(null);

/** At most this many cards under "Thẻ". */
export const CARD_LIMIT = 5;

/**
 * The "Thẻ" group (UX wave D, P7): up to five cards whose title holds every typed word, folded like the pages, for
 * a person with Workplace on the rail.
 */
function useCardGroup(groups: readonly NavGroup[], words: readonly string[]): NavGroup | null {
  const source = useContext(PaletteCardsContext);
  const reachable = useMemo(
    () => groups.some((group) => flattenGroupItems(group).some((item) => item.href === '/workplace')), [groups]);
  const asking = source !== null && reachable && words.length > 0;
  // One read per opening of the palette, at its first word: clearing the field and typing again reads nothing.
  const want = source?.want;
  const asked = useRef(false);
  useEffect(() => {
    if (!asking || asked.current) return;
    asked.current = true;
    want?.();
  }, [asking, want]);
  const cards = source?.cards;
  return useMemo(() => {
    if (!asking || cards === undefined) return null;
    const items: NavItem[] = cards
      .filter((card) => { const title = fold(card.title); return words.every((word) => title.includes(word)); })
      .slice(0, CARD_LIMIT)
      .map((card) => ({
        id: `the-${card.id}`, label: card.title, icon: card.note ? StickyNote : SquareCheckBig,
        href: `/workplace?item=${encodeURIComponent(card.id)}`,
      }));
    return items.length === 0 ? null : { id: 'the', label: 'Thẻ', icon: SquareCheckBig, items };
  }, [asking, words, cards]);
}

/**
 * The HyperTek prototype's app-wide ⌘K / Ctrl+K command palette (h-prototype
 * `components/layout/command-palette.tsx` over `components/ui/command.tsx`), with the prototype's look and
 * words. Mounted once in the app shell; lists every page this person can reach and navigates on select.
 *
 * Built here rather than on cmdk + Radix Dialog: Radix Dialog locks scrolling by writing a `<style>` element,
 * which production's `style-src 'self'` refuses. It sits on ModalDialog instead (focus in, Tab kept inside,
 * Escape and focus back to the opener) and is an ARIA combobox: the caret stays in the field, the arrow keys
 * move the highlighted option (`aria-activedescendant`), Enter follows it. Matching is the prototype's — every
 * typed word must appear in "group label + item label" — with Vietnamese folded (see `fold`).
 *
 * It is a lookup with nothing to lose, so it closes every way a person tries: Escape, the ✕ beside the field, and
 * a click on the scrim around it.
 */
export function CommandPalette({ groups }: { groups: readonly NavGroup[] }) {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onKey = (e: globalThis.KeyboardEvent) => {
      if (!(e.metaKey || e.ctrlKey) || e.key.toLowerCase() !== 'k' || e.repeat) return;
      // Another dialog is already asking something (Việc mới, Màu giao diện): a palette on top of it would
      // be a second modal fighting the first for the focus. The palette's own dialog is the exception — the
      // shortcut toggles it closed, as in the prototype.
      const modal = document.querySelector('[aria-modal="true"]');
      if (modal !== null && modal.querySelector('[data-command-palette]') === null) return;
      e.preventDefault();
      setOpen((v) => !v);
    };
    const onOpen = () => setOpen(true);
    window.addEventListener('keydown', onKey);
    window.addEventListener(OPEN_COMMAND_PALETTE_EVENT, onOpen);
    return () => {
      window.removeEventListener('keydown', onKey);
      window.removeEventListener(OPEN_COMMAND_PALETTE_EVENT, onOpen);
    };
  }, []);

  if (!open) return null;
  return <PaletteDialog groups={groups} onClose={() => setOpen(false)} />;
}

function PaletteDialog({ groups, onClose }: { groups: readonly NavGroup[]; onClose: () => void }) {
  const navigate = useNavigate();
  const baseId = useId();
  const labelId = `${baseId}-nhan`;
  const listId = `${baseId}-ket-qua`;
  const [query, setQuery] = useState('');
  const [active, setActive] = useState(0);
  const heading = useRef<HTMLHeadingElement>(null);
  const dismiss = useRef(onClose);
  useEffect(() => { dismiss.current = onClose; });

  /*
   * ModalDialog hears Escape only from inside the dialog. A click on the scrim moves the focus to the page, so an
   * Escape pressed after it reached nothing and the palette could not be closed. It now closes from the document too,
   * and the click on the scrim closes it outright (a press there keeps the caret in the field, so there is no blur).
   */
  useEffect(() => {
    const dialog = heading.current?.closest<HTMLElement>('[role="dialog"]');
    const scrim = dialog?.parentElement;
    if (!dialog || !scrim) return undefined;
    const onKey = (event: globalThis.KeyboardEvent) => {
      if (event.key !== 'Escape' || event.defaultPrevented || dialog.contains(document.activeElement)) return;
      event.preventDefault();
      dismiss.current();
    };
    const onPress = (event: MouseEvent) => { if (event.target === scrim) event.preventDefault(); };
    const onClick = (event: MouseEvent) => { if (event.target === scrim) dismiss.current(); };
    document.addEventListener('keydown', onKey);
    scrim.addEventListener('mousedown', onPress);
    scrim.addEventListener('click', onClick);
    return () => {
      document.removeEventListener('keydown', onKey);
      scrim.removeEventListener('mousedown', onPress);
      scrim.removeEventListener('click', onClick);
    };
  }, []);

  const words = useMemo(() => fold(query).split(/\s+/).filter(Boolean), [query]);
  const cardGroup = useCardGroup(groups, words);
  const results = useMemo(() => {
    const pages = [...groups, PERSONAL_GROUP]
      .map((group) => ({
        group,
        items: flattenGroupItems(group).filter((item) => {
          const haystack = fold(`${group.label} ${item.label}`);
          return words.every((word) => haystack.includes(word));
        }),
      }))
      .filter((result) => result.items.length > 0);
    // The cards come after the pages: a page is what the palette was first for.
    return cardGroup === null ? pages : [...pages, { group: cardGroup, items: cardGroup.items }];
  }, [groups, words, cardGroup]);

  const entries: Entry[] = results.flatMap(({ group, items }) => items.map((item) => ({ group, item })));
  const activeIndex = entries.length === 0 ? -1 : Math.min(active, entries.length - 1);
  const optionId = (entry: Entry) => `${baseId}-${entry.item.id}`;
  const activeEntry = activeIndex >= 0 ? entries[activeIndex] : undefined;
  const activeId = activeEntry ? optionId(activeEntry) : undefined;

  useEffect(() => {
    if (activeId === undefined) return;
    // jsdom has no scrollIntoView; a browser keeps the highlighted row in view as the arrows move it.
    document.getElementById(activeId)?.scrollIntoView?.({ block: 'nearest' });
  }, [activeId]);

  function select(entry: Entry) {
    onClose();
    if (entry.item.external) {
      window.open(entry.item.href, '_blank', 'noopener,noreferrer');
    } else {
      void navigate(entry.item.href);
    }
  }

  function onKeyDown(event: KeyboardEvent<HTMLInputElement>) {
    if (entries.length === 0) return;
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setActive((activeIndex + 1) % entries.length);
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      setActive((activeIndex - 1 + entries.length) % entries.length);
    } else if (event.key === 'Enter' && activeEntry) {
      event.preventDefault();
      select(activeEntry);
    }
  }

  let index = -1;
  return (
    <ModalDialog
      labelId={labelId}
      onClose={onClose}
      // Top-aligned, so it drops 8 px into place like "Việc mới".
      motion="drop"
      className="my-[min(20vh,10rem)] max-w-[36rem] overflow-hidden rounded-[var(--h-radius-lg)] border border-[hsl(var(--h-border))] bg-popover p-0 sm:p-0"
    >
      {/* `data-command-palette` marks this dialog as the palette's own, so the shortcut can close it. */}
      <h2 ref={heading} id={labelId} data-command-palette className="sr-only">Bảng lệnh</h2>
      <div className="flex items-center border-b border-[hsl(var(--h-border))] px-3">
        <Search className="mr-2 h-4 w-4 shrink-0 text-muted-foreground" aria-hidden="true" />
        <input
          type="text"
          role="combobox"
          aria-label="Tìm trang hoặc thẻ"
          aria-expanded="true"
          aria-controls={listId}
          aria-autocomplete="list"
          aria-activedescendant={activeId}
          autoComplete="off"
          spellCheck={false}
          placeholder="Tìm trang hoặc thẻ…"
          value={query}
          onChange={(event) => { setQuery(event.target.value); setActive(0); }}
          onKeyDown={onKeyDown}
          className="flex h-11 w-full bg-transparent py-3 text-sm text-[hsl(var(--h-foreground))] outline-none placeholder:text-muted-foreground"
        />
        <button
          type="button"
          aria-label="Đóng"
          title="Đóng (Esc)"
          onClick={onClose}
          className="-mr-1 ml-2 flex size-8 shrink-0 pointer-coarse:size-11 items-center justify-center rounded-[var(--h-radius-sm)] text-muted-foreground transition-colors hover:bg-[hsl(var(--h-muted))] hover:text-[hsl(var(--h-foreground))]"
        >
          <X className="h-4 w-4" aria-hidden="true" />
        </button>
      </div>
      <div id={listId} role="listbox" aria-label="Kết quả" className="scrollbar-thin max-h-[20rem] overflow-y-auto overflow-x-hidden p-1">
        {results.map(({ group, items }) => (
          <div key={group.id} role="group" aria-labelledby={`${baseId}-nhom-${group.id}`} className="overflow-hidden p-1 text-popover-foreground">
            <div id={`${baseId}-nhom-${group.id}`} className="px-2 py-1.5 text-xs font-medium text-muted-foreground">
              {group.label}
            </div>
            {items.map((item) => {
              index += 1;
              const position = index;
              const entry = { group, item };
              const selected = position === activeIndex;
              const Icon = item.icon;
              return (
                <div
                  key={item.id}
                  id={optionId(entry)}
                  role="option"
                  aria-selected={selected}
                  data-selected={selected}
                  onMouseMove={() => { if (!selected) setActive(position); }}
                  onClick={() => select(entry)}
                  className={cn(
                    'relative flex min-h-11 cursor-pointer select-none items-center gap-2 rounded-[var(--h-radius-sm)] px-2 py-2 text-sm outline-none',
                    'data-[selected=true]:bg-accent data-[selected=true]:text-accent-foreground',
                  )}
                >
                  <Icon className="mr-2 h-4 w-4 text-[hsl(var(--h-muted-foreground))]" aria-hidden="true" />
                  {item.label}
                </div>
              );
            })}
          </div>
        ))}
      </div>
      {entries.length === 0 && <p className="py-6 text-center text-sm text-muted-foreground">Không có kết quả.</p>}
    </ModalDialog>
  );
}
